/**
 * costs.js — Cost Payment System (5 types, atomic rollback)
 *
 * Validates and pays action costs on a deep copy of the character.
 * If ANY cost in the chain fails, the original character is returned
 * unchanged (atomic rollback).
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #174)
 */

// ─── Cost Payers ────────────────────────────────────────────────────────────

const COST_PAYERS = {
  /**
   * Mark stress on the character. Fails if would exceed maxStress.
   */
  mark_stress: (cost, character) => {
    const amount = cost.amount || 1;
    const stress = character.stress || 0;
    const maxStress = character.maxStress || 8;
    if (stress + amount > maxStress) {
      return { success: false, reason: `Cannot mark ${amount} Stress (${stress}/${maxStress})` };
    }
    character.stress = stress + amount;
    return { success: true };
  },

  /**
   * Spend hope. Fails if insufficient hope.
   */
  spend_hope: (cost, character) => {
    const amount = cost.amount || 1;
    const hope = character.hope || 0;
    if (hope < amount) {
      return { success: false, reason: `Need ${amount} Hope (have ${hope})` };
    }
    character.hope = hope - amount;
    return { success: true };
  },

  /**
   * Spend a custom token. Fails if insufficient tokens.
   * Requires context.cardKey to locate the token in cardResources.
   */
  spend_token: (cost, character, context) => {
    const { tokenId, resourceKey, amount = 1 } = cost;
    const key = resourceKey || context.cardKey;
    const token = character.cardResources?.[key]?.[tokenId];
    if (!token || token.current < amount) {
      return {
        success: false,
        reason: `Need ${amount} ${tokenId} (have ${token?.current || 0})`,
      };
    }
    token.current -= amount;
    return { success: true };
  },

  /**
   * Spend HP. Fails if insufficient HP.
   */
  spend_hp: (cost, character) => {
    const amount = cost.amount || 1;
    const hp = character.hp || 0;
    if (hp < amount) {
      return { success: false, reason: `Need ${amount} HP (have ${hp})` };
    }
    character.hp = hp - amount;
    return { success: true };
  },

  /**
   * Set a cooldown. Cooldowns are tracked externally (not on character).
   * Returns cooldown metadata for the ActionExecutor to apply.
   */
  set_cooldown: (cost) => {
    const { cooldownKey, duration } = cost;
    return {
      success: true,
      cooldown: { key: cooldownKey, until: Date.now() + duration },
    };
  },
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Validate that all costs CAN be paid without actually paying them.
 *
 * @param {object[]} costs - Array of cost objects
 * @param {object} context - { character, cardKey }
 * @returns {{ canPay: boolean, failedCost: object|null, reason: string }}
 */
export const validateCosts = (costs, context) => {
  if (!costs || costs.length === 0) {
    return { canPay: true, failedCost: null, reason: '' };
  }

  // Validate on a throwaway copy (don't commit anything)
  const tempCopy = JSON.parse(JSON.stringify(context.character));

  for (const cost of costs) {
    const payer = COST_PAYERS[cost.type];
    if (!payer) {
      return { canPay: false, failedCost: cost, reason: `Unknown cost type: ${cost.type}` };
    }
    const result = payer(cost, tempCopy, context);
    if (!result.success) {
      return { canPay: false, failedCost: cost, reason: result.reason };
    }
  }

  return { canPay: true, failedCost: null, reason: '' };
};

/**
 * Pay all costs atomically on a deep copy of the character.
 * If ANY cost fails, returns the original character unchanged.
 *
 * @param {object[]} costs - Array of cost objects
 * @param {object} context - { character, cardKey }
 * @returns {{ success: boolean, character: object, reason: string, cooldowns?: object }}
 */
export const payCosts = (costs, context) => {
  if (!costs || costs.length === 0) {
    return { success: true, character: context.character, reason: '' };
  }

  // Deep copy to avoid mutating original
  const characterCopy = JSON.parse(JSON.stringify(context.character));
  const cooldownUpdates = {};

  for (const cost of costs) {
    const payer = COST_PAYERS[cost.type];
    if (!payer) {
      // Unknown cost type — fail safe, return original unchanged
      return {
        success: false,
        character: context.character,
        reason: `Unknown cost type: ${cost.type}`,
      };
    }

    const result = payer(cost, characterCopy, context);
    if (!result.success) {
      // ANY failure → return ORIGINAL character (nothing committed)
      return {
        success: false,
        character: context.character,
        reason: result.reason,
      };
    }
    if (result.cooldown) {
      cooldownUpdates[result.cooldown.key] = result.cooldown.until;
    }
  }

  // All costs paid successfully on the copy
  return {
    success: true,
    character: characterCopy,
    reason: '',
    ...(Object.keys(cooldownUpdates).length > 0 && { cooldowns: cooldownUpdates }),
  };
};
